import { Grid, Menu, MenuItem, ListItemText, ListItemIcon, Box, Typography, makeStyles, FormControlLabel, Switch } from '@material-ui/core';
import { ArrowDropDown, Clear, Payment, PlaylistAddCheck, CloudDownload } from '@material-ui/icons';
import { Button, Toolbar, ConfirmationMenuItem, SelectOption, SelectField } from '@roc/ui';
import { observer } from 'mobx-react';
import { useRef, useState } from 'react';
import { ProtectiveAdvancesSearchBar } from '../protectiveAdvancesSearchBar';
import { PayableReceivablesTypes, PAYABLES_TABS, VENDOR_PAYMENT_TABS } from '../../types/types';
import { ToolbarInfoItem } from '../toolbarInfoItem';
import { usePayablesDashboardStore } from '../../hooks/usePayablesDashboardStore';
import { MarkAsPaidModal } from '../payables/markAsPaidModal';

const useStyles = makeStyles(theme => ({
  content: {
    fontWeight: 'bold',
    fontSize: '15px'
  },
  label: {
    fontSize: '13px',
  },
  labelBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  labelBoxCount: {
    marginRight: '25px',
    marginLeft: '25px',
  },
  labelBoxAmount: {
    marginRight: '50px',
    marginLeft: '25px',
  },
  dataPoint: {
    marginTop: '5px',
    textAlign: 'center',
  },
  containerBox: {
    display: 'flex',
  },
  infoItemsBox: {
    marginRight: '40px',
    marginLeft: '25px',
    display: 'flex',
    alignItems: 'center',
    gap: '30px'
  },
}));

export const VendorPaymentsToolbar = observer(() => {
  const classes = useStyles();
  const anchorRef = useRef(null);
  const [showBulkActionsMenu, setShowBulkActionsMenu] = useState(false);
  const { vendorPaymentDashboardStore, vendorPaymentDashboardStore: { tab, entity, globalStore } } = usePayablesDashboardStore();
  const [payableRefNumber, setPayableRefNumber] = useState<string>('');
  const [showMarkAsPaidModal, setShowMarkAsPaidModal] = useState(false);

  const closeMenu = () => {
    setShowBulkActionsMenu(false);
  };

  const openMarkAsPaidModal = () => {
    if (vendorPaymentDashboardStore.recordsIds && vendorPaymentDashboardStore.recordsIds.length > 0) {
      setShowMarkAsPaidModal(true);
    } else {
      globalStore.notificationStore.showErrorNotification({
        message: 'Please select an item from the table.',
      });
    }
  }

  const performAction = async (status, param?) => {
    if (vendorPaymentDashboardStore.recordsIds && vendorPaymentDashboardStore.recordsIds.length > 0) {
      await vendorPaymentDashboardStore.performActionsWithParam(status, param ? param : '');
      vendorPaymentDashboardStore.resetToolbarInfo();
      vendorPaymentDashboardStore.setShowSelectedRecords(false);
      vendorPaymentDashboardStore.counterPartyExpenseGridStore.resetAndFetchGridData();
    } else {
      globalStore.notificationStore.showErrorNotification({
        message: 'Please select an item from the table.',
      });
    }
  }

  const closeMarkAsPaidModal = () => {
    setPayableRefNumber('');
    setShowMarkAsPaidModal(false);
  };

  const handleMarkAsPaidConfirm = async () => {
    performAction(PayableReceivablesTypes.PAID, payableRefNumber);
    closeMarkAsPaidModal();
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <Toolbar left={
          <>
            <Box alignItems="center" display="flex" pr={2}>
              <Typography style={{ paddingRight: '16px' }}>{"Status"}: </Typography>
              <SelectField
                testId={'typeOfFee'}
                fullWidth
                variant="outlined"
                options={VENDOR_PAYMENT_TABS}
                value={tab}
                onChange={value => {
                  vendorPaymentDashboardStore.setTab(value);
                }}
                required={true}
                style={{ width: '125px' }}
              />
            </Box>
            <Box>
              <ProtectiveAdvancesSearchBar
                counterPartyExpenseStore={vendorPaymentDashboardStore}
                label={"Vendor"}
              />
            </Box>
          </>
        }>
          <Box className={classes.containerBox} >
            <Box className={classes.infoItemsBox}>
              <ToolbarInfoItem value={vendorPaymentDashboardStore.recordsIds.length} label={"Count"} />
              <ToolbarInfoItem value={`${'$'}${vendorPaymentDashboardStore.totalAmount}`} label={"Amount"} />
            </Box>
            <Box style={{ textAlign: 'center', marginRight: '25px' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={vendorPaymentDashboardStore.showSelectedRecords}
                    onChange={(e) => {
                      vendorPaymentDashboardStore.setShowSelectedRecords(!vendorPaymentDashboardStore.showSelectedRecords);
                      vendorPaymentDashboardStore.counterPartyExpenseGridStore.resetAndFetchGridData()
                    }}
                    color="primary"
                  />
                }
                label={"Show Selected"}
              />
            </Box>
            <Box style={{
              alignItems: 'center',
              justifyContent: 'center', textAlign: 'center', marginRight: '25px'
            }}>
              <Button
                id="basic-button"
                aria-controls={open ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={() => vendorPaymentDashboardStore.exportServicingDashboardData()}
                testId="open_menu"
                variant="contained"
                size="small"
                color="primary"
              >
                Export as Excel
              </Button>
            </Box>
            <Box style={{ textAlign: 'center' }}>
              <div ref={anchorRef} style={{ display: 'inline-block' }}>
                <Button
                  id="basic-button"
                  aria-controls={open ? 'basic-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                  onClick={() => { setShowBulkActionsMenu(true) }}
                  testId="open_menu"
                  variant="contained"
                  size="small"
                  color="primary"
                >
                  Bulk Actions <ArrowDropDown style={{ marginRight: '-6px' }} />
                </Button>
                <Menu
                  id="basic-menu"
                  anchorEl={anchorRef.current}
                  open={showBulkActionsMenu}
                  onClose={closeMenu}
                  keepMounted={true}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  getContentAnchorEl={null}
                >
                  <ConfirmationMenuItem
                    testId={`approve-payable`}
                    onClick={() => { performAction(PayableReceivablesTypes.APPROVED, null) }}
                    confirmDialogProps={{
                      body: `Are you sure you want to Approve the selected records?`,
                    }}
                    disabled={!globalStore.userFeatures.approveVendorPaymentAllowed}
                  >
                    <ListItemIcon>
                      <PlaylistAddCheck fontSize="small" />
                    </ListItemIcon>
                    <ListItemText onClick={closeMenu}>Approve</ListItemText>
                  </ConfirmationMenuItem>
                  <ConfirmationMenuItem
                    testId={`reject-payable`}
                    onClick={() => { performAction(PayableReceivablesTypes.REJECTED, null) }}
                    confirmDialogProps={{
                      body: `Are you sure you want to Reject the selected records?`,
                    }}
                  >
                    <ListItemIcon>
                      <Clear fontSize="small" />
                    </ListItemIcon>
                    <ListItemText onClick={closeMenu}>Reject</ListItemText>
                  </ConfirmationMenuItem>
                  <MenuItem onClick={() => openMarkAsPaidModal()}>
                    <ListItemIcon>
                      <Payment fontSize="small" />
                    </ListItemIcon>
                    <ListItemText onClick={closeMenu}>Mark as Paid</ListItemText>
                  </MenuItem>
                </Menu>
              </div>
            </Box>
          </Box>
        </Toolbar>
      </Grid>
      <MarkAsPaidModal
        payableRefNumber={payableRefNumber}
        setPayableRefNumber={setPayableRefNumber}
        showMarkAsPaidModal={showMarkAsPaidModal}
        closeMarkAsPaidModal={closeMarkAsPaidModal}
        handleMarkAsPaidConfirm={handleMarkAsPaidConfirm}
      />
    </Grid>

  );
});
